// src/pages/MerchantCombos.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { FaCalendarCheck, FaCheck, FaClock, FaHandshake, FaPaperPlane, FaPlus, FaShieldAlt, FaTimes } from 'react-icons/fa';
import { comboAction, formatMoney, normalizeComboPayload } from '../lib/combos';

const today = new Date().toISOString().slice(0, 10);

export default function MerchantCombos({ embedded = false, businessId }: { embedded?: boolean; businessId?: string }) {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<any>({ created: [], invitations: [], active: [], commission: { combo_rate: 8 } });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState<any>({
    title: '', description: '', region: 'Québec', city: '', category: 'Détente', merchant_value: 0, capacity: 10,
    conditions: '', image_url: '', invited_business_ids: '', slots: [{ date: today, start_time: '14:00', end_time: '18:00', capacity: 10 }],
  });

  const values = [Number(form.merchant_value || 0)];
  const suggested = useMemo(() => {
    const totalValue = values.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
    const price = Math.max(0, Math.round(totalValue * 0.9));
    const commission = Math.round(price * Number(dashboard?.commission?.combo_rate || 8)) / 100;
    return { totalValue, price, commission, net: Math.max(0, price - commission) };
  }, [form.merchant_value, dashboard?.commission?.combo_rate]);

  const refresh = () => {
    setLoading(true);
    comboAction('merchant_dashboard', { business_id: businessId })
      .then((res: any) => setDashboard(res))
      .catch((err) => setMessage(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, [businessId]);

  const updateSlot = (idx: number, patch: any) => setForm((f: any) => ({ ...f, slots: f.slots.map((s: any, i: number) => i === idx ? { ...s, ...patch } : s) }));
  const addSlot = () => setForm((f: any) => ({ ...f, slots: [...f.slots, { date: today, start_time: '14:00', end_time: '18:00', capacity: f.capacity || 10 }] }));

  const createCombo = async () => {
    if (!form.title.trim() || !form.description.trim()) return setMessage('Ajoute un titre et une description.');
    setSaving(true); setMessage(null);
    try {
      const invited = String(form.invited_business_ids || '').split(',').map((s) => s.trim()).filter(Boolean);
      await comboAction('create_combo', {
        combo: normalizeComboPayload({
          title: form.title, description: form.description, region: form.region, city: form.city, category: form.category,
          creator_business_id: businessId, merchant_value: Number(form.merchant_value || 0), suggested_price: suggested.price,
          capacity: Number(form.capacity || 10), image_url: form.image_url, conditions: form.conditions,
          invited_business_ids: invited, time_slots: form.slots,
        }),
      });
      setMessage('Combo créé. Les invitations marchands sont maintenant en attente.');
      setForm((f: any) => ({ ...f, title: '', description: '', invited_business_ids: '' }));
      refresh();
    } catch (e: any) { setMessage(e.message); } finally { setSaving(false); }
  };

  const respond = async (participantId: string, response: 'accepted' | 'declined') => {
    setSaving(true); setMessage(null);
    try {
      await comboAction('respond_invitation', {
        participant_id: participantId,
        response,
        legal_acceptance: response === 'accepted',
        accepted_terms_version: 'combo_terms_v1_quebec_2026',
      });
      setMessage(response === 'accepted' ? 'Participation acceptée.' : 'Participation refusée.');
      refresh();
    } catch (e: any) { setMessage(e.message); } finally { setSaving(false); }
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50 p-4 md:p-8'}>
      <div className="space-y-8">
        <div className="rounded-[2rem] bg-gradient-to-br from-slate-950 to-blue-900 text-white p-8 shadow-xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-xs font-black uppercase tracking-widest mb-4"><FaHandshake /> Combos collaboratifs</div>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight">Créez des Combos avec d’autres marchands</h2>
          <p className="mt-4 text-blue-100 max-w-3xl leading-relaxed">Invitez 1 ou 2 partenaires, acceptez un prix commun, proposez des plages horaires et publiez automatiquement un Combo Forfeo lorsque les marchands acceptent.</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div className="rounded-2xl bg-white/10 p-4"><b>{dashboard?.stats?.created_count || 0}</b><br/>créés</div>
            <div className="rounded-2xl bg-white/10 p-4"><b>{dashboard?.stats?.active_count || 0}</b><br/>actifs</div>
            <div className="rounded-2xl bg-white/10 p-4"><b>{dashboard?.stats?.pending_count || 0}</b><br/>en attente</div>
            <div className="rounded-2xl bg-white/10 p-4"><b>{dashboard?.commission?.combo_rate || 8}%</b><br/>commission Forfeo</div>
          </div>
        </div>

        {message && <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 font-bold text-blue-900">{message}</div>}

        <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_.9fr] gap-6">
          <div className="bg-white rounded-[2rem] border border-slate-200 p-6 shadow-sm">
            <h3 className="text-2xl font-black mb-5 flex items-center gap-2"><FaPlus className="text-blue-600" /> Nouveau Combo</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="rounded-2xl border p-4 font-bold" placeholder="Nom du Combo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <input className="rounded-2xl border p-4 font-bold" placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <input className="rounded-2xl border p-4 font-bold" placeholder="Région" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
              <input className="rounded-2xl border p-4 font-bold" placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <input type="number" className="rounded-2xl border p-4 font-bold" placeholder="Valeur de votre portion ($)" value={form.merchant_value} onChange={(e) => setForm({ ...form, merchant_value: e.target.value })} />
              <input type="number" className="rounded-2xl border p-4 font-bold" placeholder="Capacité" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
            </div>
            <textarea className="mt-4 w-full rounded-2xl border p-4 font-semibold min-h-[120px]" placeholder="Description client" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <textarea className="mt-4 w-full rounded-2xl border p-4 font-semibold min-h-[90px]" placeholder="Conditions importantes" value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} />
            <input className="mt-4 w-full rounded-2xl border p-4 font-semibold" placeholder="IDs des marchands à inviter, séparés par des virgules" value={form.invited_business_ids} onChange={(e) => setForm({ ...form, invited_business_ids: e.target.value })} />
            <input className="mt-4 w-full rounded-2xl border p-4 font-semibold" placeholder="URL image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />

            <div className="mt-6 rounded-[1.5rem] bg-slate-50 p-5 border border-slate-100">
              <div className="flex items-center justify-between mb-3"><h4 className="font-black flex items-center gap-2"><FaCalendarCheck /> Plages proposées</h4><button onClick={addSlot} className="text-blue-700 font-black text-sm">+ Ajouter</button></div>
              <div className="space-y-3">
                {form.slots.map((slot: any, idx: number) => (
                  <div key={idx} className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <input type="date" className="rounded-xl border p-3 font-bold" value={slot.date} onChange={(e) => updateSlot(idx, { date: e.target.value })} />
                    <input type="time" className="rounded-xl border p-3 font-bold" value={slot.start_time} onChange={(e) => updateSlot(idx, { start_time: e.target.value })} />
                    <input type="time" className="rounded-xl border p-3 font-bold" value={slot.end_time} onChange={(e) => updateSlot(idx, { end_time: e.target.value })} />
                    <input type="number" className="rounded-xl border p-3 font-bold" value={slot.capacity} onChange={(e) => updateSlot(idx, { capacity: Number(e.target.value) })} />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-[1.5rem] bg-blue-50 border border-blue-100 p-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <div><p className="text-xs uppercase font-black text-blue-400">Valeur</p><p className="font-black">{formatMoney(suggested.totalValue)}</p></div>
              <div><p className="text-xs uppercase font-black text-blue-400">Prix suggéré</p><p className="font-black">{formatMoney(suggested.price)}</p></div>
              <div><p className="text-xs uppercase font-black text-blue-400">Forfeo</p><p className="font-black">{formatMoney(suggested.commission)}</p></div>
              <div><p className="text-xs uppercase font-black text-blue-400">Net estimé</p><p className="font-black">{formatMoney(suggested.net)}</p></div>
            </div>
            <button onClick={createCombo} disabled={saving} className="mt-6 w-full rounded-2xl bg-slate-900 text-white py-4 font-black uppercase tracking-widest disabled:opacity-50"><FaPaperPlane className="inline mr-2" /> Créer et inviter</button>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-[2rem] border border-slate-200 p-6 shadow-sm">
              <h3 className="text-xl font-black mb-4 flex items-center gap-2"><FaClock className="text-amber-500" /> Invitations reçues</h3>
              {loading ? <p>Chargement...</p> : (dashboard.invitations || []).length === 0 ? <p className="text-slate-500 font-semibold">Aucune invitation en attente.</p> : (
                <div className="space-y-3">
                  {dashboard.invitations.map((inv: any) => (
                    <div key={inv.id} className="rounded-2xl border border-slate-200 p-4">
                      <p className="font-black">{inv.combo_title}</p>
                      <p className="text-sm text-slate-500">Prix : {formatMoney(inv.suggested_price)} · Commission Forfeo : {inv.commission_rate || 8}%</p>
                      <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600"><FaShieldAlt className="inline mr-1" /> En acceptant, vous confirmez votre portion, vos disponibilités et la commission applicable.</div>
                      <div className="mt-3 flex gap-2"><button onClick={() => respond(inv.id, 'accepted')} className="flex-1 rounded-xl bg-emerald-600 text-white py-3 font-black"><FaCheck className="inline mr-1" /> Accepter</button><button onClick={() => respond(inv.id, 'declined')} className="flex-1 rounded-xl bg-slate-100 text-slate-700 py-3 font-black"><FaTimes className="inline mr-1" /> Refuser</button></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-[2rem] border border-slate-200 p-6 shadow-sm">
              <h3 className="text-xl font-black mb-4">Mes Combos actifs / en création</h3>
              <div className="space-y-3">
                {[...(dashboard.created || []), ...(dashboard.active || [])].slice(0, 8).map((c: any) => (
                  <div key={c.id} className="rounded-2xl bg-slate-50 p-4 flex items-center justify-between gap-3">
                    <div><p className="font-black">{c.title}</p><p className="text-xs text-slate-500 font-bold">{c.status} · {formatMoney(c.suggested_price)}</p></div>
                    <a href={`/combos/${c.slug || c.id}`} target="_blank" rel="noreferrer" className="text-blue-700 font-black text-sm">Voir</a>
                  </div>
                ))}
                {(!dashboard.created?.length && !dashboard.active?.length) && <p className="text-slate-500 font-semibold">Aucun Combo créé pour le moment.</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
